import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Markdown } from '../../lib/markdown';
import { Icon } from '../../ui/Icon';
import s from './Notes.module.css';

interface NotesProps {
  value: string;
  onSave: (next: string) => void;
  placeholder?: string;
  /** Larger type for a trip's main notes. */
  large?: boolean;
}

/** Notes that read as formatted text and turn into a plain editor when tapped. */
export function Notes({ value, onSave, placeholder = 'Add notes', large }: NotesProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const area = useRef<HTMLTextAreaElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: grow to fit whenever the text or mode changes
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, editing]);

  useEffect(() => {
    if (editing) area.current?.focus();
  }, [editing]);

  const commit = (text: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (text !== value) onSave(text);
  };

  const classes = {
    checklist: s.checklist,
    check: s.check,
    done: s.done,
    bullets: s.bullets,
    heading: s.heading,
    para: s.para,
  };

  if (editing) {
    return (
      <div className={`${s.notes} ${large ? s.large : ''}`}>
        <textarea
          ref={area}
          className={s.editor}
          value={draft}
          placeholder={placeholder}
          aria-label="Notes"
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => commit(next), 1200);
          }}
          onBlur={() => {
            commit(draft);
            setEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') (e.target as HTMLTextAreaElement).blur();
          }}
        />
        <p className={`${s.help} t-caption`}>
          Use - [ ] for a checklist, **bold**, and links. Saves as you type.
        </p>
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: notes hold links and checkboxes, which can't sit inside a <button>
    <div
      className={`${s.notes} ${s.view} ${large ? s.large : ''}`}
      role="button"
      tabIndex={0}
      aria-label={value ? 'Edit notes' : placeholder}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a, input')) return;
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) setEditing(true);
      }}
    >
      {value.trim() ? (
        <Markdown text={value} onToggle={onSave} classes={classes} className={s.body} />
      ) : (
        <span className={s.placeholder}>
          <Icon name="note-pencil" size={18} />
          {placeholder}
        </span>
      )}
    </div>
  );
}
